import React, { useContext, useCallback } from 'react';
import { Grid } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import SectionCard from '../SectionCard';
import SubCard from './SubCard';
import tokens from '../../../theme/tokens';
import { FormContext } from '../../../context/FormContext';
import { useLanguage } from '../../../localization';

/**
 * Factory function that creates section card components with consistent structure
 * @param {Object} config - Configuration for the section card
 * @returns {Function} A component that renders a section card
 */
const SectionCardFactory = (config) => {
  // Define the component function first
  const GeneratedSectionCardComponent = ({ onRemove, dragHandle, onSelect, isSelected }) => {
    const { formData, updateFormData } = useContext(FormContext);
    const { t } = useLanguage();

    const initialItem = () => ({
      id: uuidv4(),
      expanded: true,
      ...config.initialItemFields(),
    });

    const handleChange = useCallback((index, name, value) => {
      const currentItems = formData[config.sectionKey] || [];
      const newItems = currentItems.map((currentItem, idx) => {
        if (idx === index) {
          return { ...currentItem, [name]: value };
        }
        return currentItem;
      });
      updateFormData(config.sectionKey, newItems);
    }, [formData, updateFormData, config.sectionKey]);

    const renderSubCard = ({
      item,
      index,
      provided,
      removeItem,
    }) => {
      const handleFieldChange = (name, value) => {
        handleChange(index, name, value);
      };

      const title = item ? config.generateTitle(item, index, t) : `Item ${index + 1}`;

      return (
        <SubCard
          item={item}
          index={index}
          provided={provided}
          removeItem={() => removeItem(item.id)}
          title={title}
        >
          <Grid container spacing={tokens.spacing[2]}>
            {config.renderFields({ item, handleChange: handleFieldChange, t })}
          </Grid>
        </SubCard>
      );
    };

    // Note: removeItem used in renderSubCard needs to be stable if passed from parent.
    // Assuming it comes from SectionCard, which now reads directly from context.
    // The actual removeItem function called inside renderSubCard -> SubCard
    // should be the one defined within SectionCard, which updates context directly.

    return (
      <SectionCard
        sectionName={t(`inputSection.sections.${config.sectionName}`) || config.sectionName}
        sectionKey={config.sectionKey}
        initialItem={initialItem}
        renderSubCard={renderSubCard}
        dragHandle={dragHandle}
        onRemove={onRemove} // This prop needs to be stable from InputSection
        onSelect={onSelect}
        isSelected={isSelected}
      />
    );
  };

  // Return the memoized version of the component
  return React.memo(GeneratedSectionCardComponent);
};

export default SectionCardFactory; 